import { useEffect, useState } from 'react';
import { runsApi, type SrDetail } from '../../lib/runsApi';
import { TrainingBadge } from '../../components/TrainingBadge';

interface Props {
  srKey: string;
  /** If the parent already resolved the SR, pass it here to skip the GET. */
  initial?: SrDetail | null;
  /** Called after the run row is created server-side. */
  onCreated: (runId: string) => void;
  onCancel: () => void;
}

/**
 * Inline picker used by both AssignedSrsDialog and CustomSrDialog. It
 * resolves the SR (if not already passed in), shows the TEXEC list, and
 * creates the run on click. Naming this `TexecPicker` keeps it framework
 * -of-just-this-folder; no callers exist outside `features/home`.
 */
export function TexecPicker({ srKey, initial, onCreated, onCancel }: Props) {
  const [sr, setSr] = useState<SrDetail | null>(initial ?? null);
  const [loading, setLoading] = useState(!initial);
  const [error, setError] = useState<string | null>(null);
  const [creating, setCreating] = useState<string | null>(null);

  useEffect(() => {
    if (initial) {
      setSr(initial);
      return;
    }
    setLoading(true);
    setError(null);
    runsApi
      .loadSalesOrder(srKey)
      .then(setSr)
      .catch((err) => setError(err.message))
      .finally(() => setLoading(false));
  }, [srKey, initial]);

  const onPickTexec = async (texecKey: string) => {
    if (!sr) return;
    setCreating(texecKey);
    setError(null);
    try {
      const run = await runsApi.create(sr.srKey, texecKey);
      onCreated(run.id);
    } catch (err) {
      setError((err as Error).message);
      setCreating(null);
    }
  };

  return (
    <div className="texec-picker">
      {loading && <p className="muted">Loading SR details...</p>}
      {error && (
        <div className="card" style={{ borderColor: 'var(--bad)' }}>
          {error}
        </div>
      )}

      {sr && (
        <>
          <div className="row" style={{ flexWrap: 'wrap', gap: 6 }}>
            <strong className="mono">{sr.srKey}</strong>
            <TrainingBadge keyValue={sr.srKey} />
            <span className="badge">{sr.studio.name}</span>
            <span className="badge info">{sr.status}</span>
            {sr.orderKey && (
              <>
                <span className="badge">Order: {sr.orderKey}</span>
                <TrainingBadge keyValue={sr.orderKey} />
              </>
            )}
            {!sr.gitlabConfigured && (
              <span className="badge bad">No GitLab project mapped for this studio</span>
            )}
          </div>
          <div style={{ marginTop: 4 }}>{sr.srSummary}</div>
          <div className="muted small" style={{ marginTop: 2 }}>
            Game: {sr.gameName || '(missing)'} &middot; Markets:{' '}
            {sr.jurisdictions.length ? sr.jurisdictions.join(', ') : 'none'}
          </div>

          <h4 style={{ marginTop: 12, marginBottom: 8 }}>
            Pick a TEXEC ({sr.texecs.length})
          </h4>
          {sr.texecs.length === 0 && (
            <p className="muted">No execution tasks linked to this order.</p>
          )}
          <div className="texec-picker-list">
            {sr.texecs.map((t) => (
              <button
                key={t.key}
                className="texec-picker-row"
                disabled={creating !== null}
                onClick={() => onPickTexec(t.key)}
              >
                <strong className="mono">{t.key}</strong>
                <TrainingBadge keyValue={t.key} />
                <span className="muted texec-picker-summary">{t.summary}</span>
                {t.gameType && <span className="badge">{t.gameType}</span>}
                <span className="badge">
                  {t.criticalFiles} critical {t.criticalFiles === 1 ? 'file' : 'files'}
                </span>
                {creating === t.key && <span className="muted small">Creating...</span>}
              </button>
            ))}
          </div>

          <div className="row" style={{ marginTop: 12, justifyContent: 'flex-end' }}>
            <button className="btn btn-ghost" onClick={onCancel} disabled={creating !== null}>
              Back
            </button>
          </div>
        </>
      )}
    </div>
  );
}
